// ======================================================================
// FILE: src/modules/wms/template/pages/TemplateListPage.js
// MODULE: Work Management System (WMS)
// FEATURE: Template Management
// PURPOSE:
//   Template Listing Screen
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import wmsTemplateService
from "../../services/wmsTemplateService";

export default function TemplateListPage() {

  const navigate = useNavigate();

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [pagination, setPagination] =
    useState({});

  const [summary, setSummary] =
    useState({
      totalTemplates: 0,
      activeTemplates: 0,
      approvalRequired: 0,
      assignedTeams: 0,
    });

  const [filters, setFilters] =
    useState({
      q: "",
      work_item_type: "",
      page: 1,
      per_page: 20,
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  async function loadData() {

    try {

      setLoading(true);

      const result =
        await wmsTemplateService
          .listTemplates(filters);

      const items =
        result.items || [];

      setRows(items);

      setPagination(
        result.pagination || {}
      );

      let activeCount = 0;
      let approvalCount = 0;
      let assignedCount = 0;

      items.forEach((row) => {

        if (row.is_active) {
          activeCount += 1;
        }

        if (row.approval_required) {
          approvalCount += 1;
        }

        if (row.auto_assign_team_id) {
          assignedCount += 1;
        }

      });

      setSummary({

        totalTemplates:
          result.pagination?.total
          || items.length,

        activeTemplates:
          activeCount,

        approvalRequired:
          approvalCount,

        assignedTeams:
          assignedCount,
      });

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to load templates"
      );

    } finally {

      setLoading(false);
    }
  }

  useEffect(() => {

    loadData();

  }, [filters.page]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  function handleSearch() {

    setFilters((prev) => ({
      ...prev,
      page: 1,
    }));

    loadData();
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete Template ${row.template_name}?`
    );

    if (!ok) {
      return;
    }

    try {

      await wmsTemplateService
        .deleteTemplate(row.id);

      await loadData();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  // ==========================================================
  // PAGINATION
  // ==========================================================

  function changePage(page) {

    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="container-fluid">

      {/* ==================================================== */}
      {/* PAGE HEADER */}
      {/* ==================================================== */}

      <div
        className="card border-0 mb-4"
        style={{
          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",
          borderRadius: "12px",
          color: "#fff",
        }}
      >

        <div
          className="
            card-body
            d-flex
            justify-content-between
            align-items-center
          "
        >

          <div>

            <h2 className="mb-1">
              Template Management
            </h2>

            <div>
              Manage reusable work templates
            </div>

          </div>

          <div>

            <button
              className="btn btn-light me-2"
              onClick={loadData}
            >
              <i className="fas fa-sync-alt me-2" />
              Refresh
            </button>

            <Link
              to="/wms/templates/create"
              className="btn btn-outline-light"
            >
              <i className="fas fa-plus me-2" />
              Create Template
            </Link>

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* KPI CARDS */}
      {/* ==================================================== */}

      <div className="row mb-4">

        <div className="col-md-3">

          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #0d6efd",
            }}
          >

            <div className="card-body d-flex justify-content-between">

              <div>

                <div className="text-muted">
                  Total Templates
                </div>

                <h2>
                  {summary.totalTemplates}
                </h2>

              </div>

              <i
                className="fas fa-clipboard-list"
                style={{
                  fontSize: 28,
                  color: "#0d6efd",
                }}
              />

            </div>

          </div>

        </div>

        <div className="col-md-3">

          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #28a745",
            }}
          >

            <div className="card-body d-flex justify-content-between">

              <div>

                <div className="text-muted">
                  Active Templates
                </div>

                <h2>
                  {summary.activeTemplates}
                </h2>

              </div>

              <i
                className="fas fa-check-circle"
                style={{
                  fontSize: 28,
                  color: "#28a745",
                }}
              />

            </div>

          </div>

        </div>

        <div className="col-md-3">

          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #6f42c1",
            }}
          >

            <div className="card-body d-flex justify-content-between">

              <div>

                <div className="text-muted">
                  Approval Required
                </div>

                <h2>
                  {summary.approvalRequired}
                </h2>

              </div>

              <i
                className="fas fa-user-check"
                style={{
                  fontSize: 28,
                  color: "#6f42c1",
                }}
              />

            </div>

          </div>

        </div>

        <div className="col-md-3">

          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #fd7e14",
            }}
          >

            <div className="card-body d-flex justify-content-between">

              <div>

                <div className="text-muted">
                  Assigned Teams
                </div>

                <h2>
                  {summary.assignedTeams}
                </h2>

              </div>

              <i
                className="fas fa-users"
                style={{
                  fontSize: 28,
                  color: "#fd7e14",
                }}
              />

            </div>

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* SEARCH */}
      {/* ==================================================== */}

      <div
        className="card shadow-sm mb-4"
        style={{
          borderRadius: "12px",
        }}
      >

        <div className="card-body">

          <div className="row">

            <div className="col-md-4">

              <label>
                Search
              </label>

              <input
                className="form-control"
                value={filters.q}
                placeholder="Code / Name"
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    q: e.target.value,
                  })
                }
              />

            </div>

            <div className="col-md-3">

              <label>
                Work Item Type
              </label>

              <select
                className="form-control"
                value={
                  filters.work_item_type
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    work_item_type:
                      e.target.value,
                  })
                }
              >

                <option value="">
                  All
                </option>

                <option value="TASK">
                  Task
                </option>

                <option value="AUDIT">
                  Audit
                </option>

                <option value="OBSERVATION">
                  Observation
                </option>

                <option value="CAPA">
                  CAPA
                </option>

              </select>

            </div>

            <div
              className="
                col-md-2
                d-flex
                align-items-end
              "
            >

              <button
                className="btn btn-primary"
                onClick={handleSearch}
              >
                Search
              </button>

            </div>

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* GRID */}
      {/* ==================================================== */}

      <div
        className="card shadow-sm"
        style={{
          borderRadius: "12px",
        }}
      >

        <div className="card-body p-0">

          <table
            className="
              table
              table-hover
              table-striped
              align-middle
              mb-0
            "
          >

            <thead className="table-light">

              <tr>

                <th>
                  Template Code
                </th>

                <th>
                  Template Name
                </th>

                <th>
                  Work Item Type
                </th>

                <th>
                  Priority
                </th>

                <th>
                  Approval
                </th>

                <th>
                  Status
                </th>

                <th width="220">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    Loading...
                  </td>

                </tr>

              )}

              {!loading &&
                rows.length === 0 && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    No Records Found
                  </td>

                </tr>

              )}

              {!loading &&
                rows.map((row) => (

                <tr key={row.id}>

                  <td>
                    {row.template_code}
                  </td>

                  <td>
                    {row.template_name}
                  </td>

                  <td>

                    <span className="badge bg-info">

                      {row.work_item_type}

                    </span>

                  </td>

                  <td>

                    <span className="badge bg-warning text-dark">

                      {row.priority}

                    </span>

                  </td>

                  <td>

                    <span
                      className={
                        row.approval_required
                          ? "badge bg-primary"
                          : "badge bg-secondary"
                      }
                    >

                      {
                        row.approval_required
                          ? "Yes"
                          : "No"
                      }

                    </span>

                  </td>

                  <td>

                    <span
                      className={
                        row.is_active
                          ? "badge bg-success"
                          : "badge bg-danger"
                      }
                    >

                      {
                        row.is_active
                          ? "Active"
                          : "Inactive"
                      }

                    </span>

                  </td>

                  <td>

                    <button
                      className="
                        btn
                        btn-sm
                        btn-primary
                        me-1
                      "
                      onClick={() =>
                        navigate(
                          `/wms/templates/${row.id}/edit`
                        )
                      }
                    >
                      <i className="fas fa-folder-open me-1" />
                      Open
                    </button>

                    <button
                      className="
                        btn
                        btn-sm
                        btn-danger
                      "
                      onClick={() =>
                        handleDelete(row)
                      }
                    >
                      <i className="fas fa-trash me-1" />
                      Delete
                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

        {/* ================================================== */}
        {/* PAGINATION */}
        {/* ================================================== */}

        <div className="card-footer">

          <div
            className="
              d-flex
              justify-content-between
              align-items-center
            "
          >

            <div>

              Total:
              {" "}
              {pagination.total || 0}

            </div>

            <div>

              <button
                className="
                  btn
                  btn-sm
                  btn-outline-secondary
                  me-2
                "
                disabled={
                  (pagination.page || 1) <= 1
                }
                onClick={() =>
                  changePage(
                    (pagination.page || 1) - 1
                  )
                }
              >
                Previous
              </button>

              <span>

                Page
                {" "}
                {pagination.page || 1}
                {" "}
                of
                {" "}
                {pagination.pages || 1}

              </span>

              <button
                className="
                  btn
                  btn-sm
                  btn-outline-secondary
                  ms-2
                "
                disabled={
                  (pagination.page || 1)
                  >=
                  (pagination.pages || 1)
                }
                onClick={() =>
                  changePage(
                    (pagination.page || 1) + 1
                  )
                }
              >
                Next
              </button>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}